/**
 * Helpers for Crash List server-side pagination + sort (`GET /crash/all`).
 * Backend contract: www/src/docs/api-crash-list-pagination-prompt.md
 */

export const CRASH_LIST_DEFAULT_PAGE_SIZE = 25

export const CRASH_LIST_PAGE_SIZE_OPTIONS = [10, 25, 50]

/** Grid field / crash Label → DbFieldName when Auth/Init element has no db field. */
const CRASH_LIST_LABEL_TO_DB_FIELD = {
  organization: 'organization',
  'Crash id': 'crash_id',
  'Crash date': 'crash_date',
  'Report date': 'report_date',
  'Crash severity': 'crash_severity',
  'merge_date': 'merge_date',
  merge_date: 'merge_date',
  user_id: 'user_id',
}

/**
 * @param {unknown} elem
 * @returns {string}
 */
function getConfigElementDbFieldName (elem) {
  if (!elem || typeof elem !== 'object') return ''
  const raw =
    elem.dbFieldName ??
    elem.DbFieldName ??
    elem.db_field_name ??
    elem.db_field ??
    elem.dbField ??
    ''
  return String(raw).trim()
}

/**
 * @param {Record<string, unknown>|null|undefined} configuration
 * @param {string} field
 * @returns {string|null}
 */
function resolveCrashSortDbField (configuration, field) {
  const fieldKey = String(field ?? '').trim()
  if (!fieldKey) return null

  if (fieldKey === 'organization') {
    return 'organization'
  }

  const crashElements = configuration?.crash?.elements
  if (Array.isArray(crashElements)) {
    const elem = crashElements.find((el) => String(el?.label ?? '') === fieldKey)
    if (elem) {
      const fromConfig = getConfigElementDbFieldName(elem)
      if (fromConfig) return fromConfig
      const fromLabelMap = CRASH_LIST_LABEL_TO_DB_FIELD[fieldKey]
      if (fromLabelMap) return fromLabelMap
      // Backend also accepts structure Label for Target=crash
      return fieldKey
    }
  }

  return CRASH_LIST_LABEL_TO_DB_FIELD[fieldKey] ?? null
}

/**
 * Maps DataGrid sortModel to `/crash/all` query params.
 * Empty / unsupported sort → null (omit sort & order).
 *
 * @param {Array<{ field?: string, sort?: 'asc'|'desc'|null }>|null|undefined} sortModel
 * @param {Record<string, unknown>|null|undefined} configuration
 * @returns {{ sort: string, order: 'ASC'|'DESC' }|null}
 */
export function mapCrashListSortToQuery (sortModel, configuration) {
  if (!Array.isArray(sortModel) || sortModel.length === 0) return null
  const entry = sortModel[0]
  if (!entry?.field || (entry.sort !== 'asc' && entry.sort !== 'desc')) return null

  const sort = resolveCrashSortDbField(configuration, entry.field)
  if (!sort) return null

  return {
    sort,
    order: entry.sort === 'desc' ? 'DESC' : 'ASC',
  }
}

/**
 * Appends sort/order to URL when sortModel maps to a crash field.
 *
 * @param {URL} url
 * @param {Array<{ field?: string, sort?: 'asc'|'desc'|null }>|null|undefined} sortModel
 * @param {Record<string, unknown>|null|undefined} configuration
 */
export function appendCrashListSortParams (url, sortModel, configuration) {
  const mapped = mapCrashListSortToQuery(sortModel, configuration)
  if (!mapped) return
  url.searchParams.set('sort', mapped.sort)
  url.searchParams.set('order', mapped.order)
}

/**
 * Crash-section column labels (plus organization) that support server sort.
 *
 * @param {Record<string, unknown>|null|undefined} configuration
 * @returns {Set<string>}
 */
export function getCrashListSortableFieldSet (configuration) {
  const fields = new Set(['organization'])
  const crashElements = configuration?.crash?.elements
  if (Array.isArray(crashElements)) {
    for (const elem of crashElements) {
      const label = String(elem?.label ?? '').trim()
      if (label) fields.add(label)
    }
  }
  return fields
}

/** DataGrid operator → API option (`section.Label.option`). */
const DATAGRID_OPERATOR_TO_API = {
  equals: 'equal',
  is: 'equal',
  '=': 'equal',
  equal: 'equal',
  contains: 'contains',
  startsWith: 'startsWith',
  endsWith: 'endsWith',
  '!=': 'notEqual',
  not: 'notEqual',
  after: 'from',
  onOrAfter: 'from',
  before: 'to',
  onOrBefore: 'to',
}

/**
 * @param {Record<string, unknown>|null|undefined} configuration
 * @param {string} field
 * @returns {{ section: string, label: string }|null}
 */
export function resolveCrashListFilterField (configuration, field) {
  const fieldKey = String(field ?? '').trim()
  if (!fieldKey || fieldKey === 'organization' || fieldKey === 'validated_lock' || fieldKey === 'id') {
    return null
  }

  const config = configuration && typeof configuration === 'object' ? configuration : {}
  for (const section of Object.keys(config)) {
    const elements = config[section]?.elements
    if (!Array.isArray(elements)) continue
    if (elements.some((el) => String(el?.label ?? '') === fieldKey)) {
      return { section, label: fieldKey }
    }
  }
  return null
}

/**
 * @param {unknown} value
 * @returns {string|null}
 */
function serializeCrashListFilterValue (value) {
  if (value == null || value === '') return null
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString()
  }
  if (typeof value === 'object' && value !== null && typeof value.toISOString === 'function') {
    try {
      return value.toISOString()
    } catch {
      /* fall through */
    }
  }
  if (Array.isArray(value)) {
    // isAnyOf not supported server-side yet
    return null
  }
  return String(value)
}

/**
 * Maps DataGrid filterModel to `/crash/all` field query params.
 * Panel Filters use the same `section.Label.option` shape.
 *
 * @param {{ items?: Array<{ field?: string, operator?: string, value?: unknown }> }|null|undefined} filterModel
 * @param {Record<string, unknown>|null|undefined} configuration
 * @returns {Record<string, string>}
 */
export function mapCrashListFilterModelToQueryParams (filterModel, configuration) {
  const items = filterModel?.items
  if (!Array.isArray(items) || items.length === 0) return {}

  /** @type {Record<string, string>} */
  const params = {}

  for (const item of items) {
    const field = String(item?.field ?? '').trim()
    const operator = String(item?.operator ?? '').trim()
    if (!field || !operator) continue

    const apiOption = DATAGRID_OPERATOR_TO_API[operator]
    if (!apiOption) continue

    const resolved = resolveCrashListFilterField(configuration, field)
    if (!resolved) continue

    const serialized = serializeCrashListFilterValue(item.value)
    if (serialized == null) continue

    params[`${resolved.section}.${resolved.label}.${apiOption}`] = serialized
  }

  return params
}

/**
 * Merges StatisticsFilterPanel params with DataGrid column filter params.
 * Grid params win on key collision.
 *
 * @param {Record<string, string>|null|undefined} panelFilters
 * @param {Record<string, string>|null|undefined} gridFilters
 * @returns {Record<string, string>|null}
 */
export function mergeCrashListFilterParams (panelFilters, gridFilters) {
  const panel =
    panelFilters && typeof panelFilters === 'object' && !Array.isArray(panelFilters)
      ? panelFilters
      : {}
  const grid =
    gridFilters && typeof gridFilters === 'object' && !Array.isArray(gridFilters)
      ? gridFilters
      : {}
  const merged = { ...panel, ...grid }
  return Object.keys(merged).length > 0 ? merged : null
}

/**
 * @param {number} page zero-based
 * @param {number} pageSize
 * @returns {{ limit: number, offset: number }}
 */
export function getCrashListLimitOffset (page, pageSize) {
  const safePage = Number.isFinite(page) && page >= 0 ? Math.floor(page) : 0
  const safeSize =
    Number.isFinite(pageSize) && pageSize > 0 ? Math.floor(pageSize) : CRASH_LIST_DEFAULT_PAGE_SIZE
  return {
    limit: safeSize,
    offset: safePage * safeSize,
  }
}

/**
 * Reads total distinct crash count from `/crash/all` responseData.
 * Prefers totalCount; accepts total_count (package-exports style).
 *
 * @param {Record<string, unknown>|null|undefined} responseData
 * @returns {number|null}
 */
export function parseCrashListTotalCount (responseData) {
  if (!responseData || typeof responseData !== 'object') return null
  const raw = responseData.totalCount ?? responseData.total_count
  if (raw == null || raw === '') return null
  const n = Number(raw)
  return Number.isFinite(n) && n >= 0 ? n : null
}

/**
 * Reads count from `/crash/count` responseData (`{ count }`).
 *
 * @param {Record<string, unknown>|null|undefined} responseData
 * @returns {number|null}
 */
export function parseCrashCountResponse (responseData) {
  if (!responseData || typeof responseData !== 'object') return null
  const raw = responseData.count
  if (raw == null || raw === '') return null
  const n = Number(raw)
  return Number.isFinite(n) && n >= 0 ? n : null
}

/**
 * Distinct crash ids in a table-format page (for toolbar / fallbacks).
 *
 * @param {unknown[]} crashes
 * @returns {number}
 */
export function countDistinctCrashesInPage (crashes) {
  if (!Array.isArray(crashes) || crashes.length === 0) return 0
  const ids = new Set()
  for (const crash of crashes) {
    const id = crash?.crash?.['Crash id'] ?? crash?.['Crash id']
    if (id != null && id !== '') ids.add(String(id))
  }
  return ids.size
}

/**
 * Fallback rowCount when backend has no totalCount yet:
 * if the page is "full", assume at least one more page; else exact end.
 *
 * @param {{ page: number, pageSize: number, pageDistinctCount: number }} params
 * @returns {number}
 */
export function estimateCrashListRowCount ({ page, pageSize, pageDistinctCount }) {
  const safePage = Math.max(0, page)
  const safeSize = Math.max(1, pageSize)
  const loadedBefore = safePage * safeSize
  if (pageDistinctCount < safeSize) {
    return loadedBefore + pageDistinctCount
  }
  return loadedBefore + pageDistinctCount + 1
}
